import { calculateAge } from '../components/utils/timeFormat';

export type AgeBracket = 'minor' | 'adult';

/**
 * Which age bracket a viewer belongs to, resolved fail-closed.
 *
 * `ageGroup` is written server-side by the onUserDobWritten Cloud Function and
 * is the authority Firestore rules compare against; everything below it is a
 * best guess for the brief window before that trigger has run. Anything unknown
 * or unparseable resolves to 'minor', matching the rules' own default — a wrong
 * guess in that direction shows a minor too little, and in the other direction
 * puts a minor in a room full of adults.
 *
 * Extracted because this ladder was already written out by hand in Discover and
 * needed for Rooms in three more places; four copies of a fail-closed safety
 * check is three too many.
 */
export function resolveAgeBracket(profile: any): AgeBracket {
    const stored = profile?.ageGroup;
    if (stored === 'adult' || stored === 'minor') return stored;

    const age = calculateAge(profile?.dateOfBirth);
    if (age !== null && age !== undefined) return age >= 18 ? 'adult' : 'minor';

    const legacyAge = profile?.age;
    if (typeof legacyAge === 'number' && Number.isFinite(legacyAge)) {
        return legacyAge >= 18 ? 'adult' : 'minor';
    }

    return 'minor';
}

/** Normalises a stored value, defaulting anything unrecognised to 'minor'. */
export function normalizeBracket(value: any): AgeBracket {
    return value === 'adult' ? 'adult' : 'minor';
}

/**
 * Whether a viewer may see content belonging to a given bracket.
 *
 * Deliberately strict rather than "adults see everything": an adult has no more
 * business in a room of minors than the reverse.
 */
export function sameBracket(viewer: any, contentAgeGroup: any): boolean {
    return resolveAgeBracket(viewer) === normalizeBracket(contentAgeGroup);
}
